<script setup lang="ts">
  import UiTag from '@/components/UiTag/UiTag.vue'
  import type { IProps } from './UiFileDownload.types'
  import { EVARIANT_BUTTON } from '@/components/UiButton/UiButton.types'
  import { EFILE_DOWNLOAD_VARIANT } from '@/components/UiFileDownload/UiFileDownload.types'

  const DEFAULT_BUTTON_ICON = '/icons/UiFileDownload/arrow_right.svg'
  const FILE_EXTENSION_PATTERN = /\.([a-z0-9]{2,5})$/i

  const props = withDefaults(defineProps<IProps>(), {
    variant: EFILE_DOWNLOAD_VARIANT.DEFAULT,
    button: {
      icon: DEFAULT_BUTTON_ICON,
      size: 'small',
      target: '_self',
    },
  })

  const buttonIcon = computed(() => props.button?.icon ?? DEFAULT_BUTTON_ICON)

  const formatLabel = computed(() => {
    const fileName = props.button?.to?.split(/[?#]/)[0]?.split('/').pop() ?? ''
    const extension = fileName.match(FILE_EXTENSION_PATTERN)?.[1]
      ?? props.format?.split('/').pop()

    return extension?.toUpperCase() ?? ''
  })
</script>

<template>
  <div :class="['fileDownload', `fileDownload--${variant}`]">
    <div class="fileDownload__container">
      <p
        v-if="label"
        class='fileDownload__label'
      >
        {{ label }}
      </p>

      <UiTag
        v-if="formatLabel"
        class="fileDownload__format"
        :label="formatLabel"
      />

      <p
        v-if="size"
        class="fileDownload__size"
      >
        {{ size }}
      </p>
    </div>

    <UiButton
      :to="button.to"
      :variant="EVARIANT_BUTTON.SECONDARY"
      :size="button.size"
      :target="button.target"
      class="fileDownload__button"
    >
      <span>
        {{ button.text }}
      </span>

      <img
        v-if="buttonIcon"
        class="fileDownload__arrow"
        :src="buttonIcon"
        alt=""
        title=""
      >
    </UiButton>
  </div>
</template>

<style lang="scss" scoped>
  .fileDownload {
    $root: &;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    padding: 0.8rem;
    border-radius: 0.4rem;

    &:hover {
      background-color: $gray-100;
    }

    &__label {
      @include body-2;
      color: $text-400;
    }
    
    &__arrow {
      padding-left: .8rem;
    }

    &__size {
      @include body-3;
      display: inline-block;
      color: $text-300;
    }

    &__format {
      display: inline-block;
      margin-right: .8rem;
    }

    &__button {
      width: 100%;
      white-space: nowrap;
      transition: border-color 0.2s ease;

      &:hover {
        border-color: $text-300;
      }
    }
    
    &--simple {
      #{$root}__format, #{$root}__size {
        display: none;
      }
    }

    &--default {
      width: 100%;
      margin-bottom: 1.6rem;

      #{$root}__container {
        width: 100%;
        margin-bottom: 1.2rem;
      }
    }

    @include atXS {
      &--default {
        flex-direction: row;
        width: auto;

        #{$root}__container {
          width: 100%;
          margin-bottom: 1.2rem;
        }
        
        #{$root}__button {
          width:auto;
        }
      }
    }
  }
</style>
